import { registerComponent } from "helpers";
import WYATTElement from "wyatt";

registerComponent(
  "smooth-anchor-scroll",
  class SmoothAnchorScroll extends WYATTElement {
    CONTENT_GROUP = "[data-content-group]";

    links = [];
    targets = [];
    navigationEnabled = false;
    observer = null;
    intersecting = new Set();
    suppressObserverUntil = 0;
    resizeRaf = 0;
    mq = window.matchMedia("(min-width: 1024px)");

    connectedCallback() {
      this.buildAnchors();

      if (!this.navigationEnabled) return;

      this.links.forEach((anchor) => {
        anchor.addEventListener("click", this.handleClick);
      });

      this.syncHeaderVar();
      this.mq.addEventListener("change", this.syncHeaderVar);
      window.addEventListener("resize", this.handleResize);
    }

    disconnectedCallback() {
      this.links.forEach((anchor) => {
        anchor.removeEventListener("click", this.handleClick);
      });

      this.observer?.disconnect();
      this.observer = null;
      this.mq.removeEventListener("change", this.syncHeaderVar);
      window.removeEventListener("resize", this.handleResize);

      if (this.resizeRaf) {
        cancelAnimationFrame(this.resizeRaf);
        this.resizeRaf = 0;
      }
    }

    sectionHasContent(section) {
      const groups = section.querySelectorAll(this.CONTENT_GROUP);

      if (groups.length === 0) return true;

      return Array.from(groups).some((group) => group.childElementCount > 0);
    }

    buildAnchors() {
      const targetsContainer = this.querySelector("[data-targets]");
      const anchorsNav = this.querySelector(".product-content__anchors");
      const navList = this.querySelector(".product-content__list");

      if (!targetsContainer) return;

      const sections = Array.from(
        targetsContainer.querySelectorAll("section[id]"),
      );

      this.targets = [];

      sections.forEach((section) => {
        if (!this.sectionHasContent(section)) {
          section.hidden = true;
          return;
        }

        this.targets.push(section);
      });

      this.navigationEnabled = this.targets.length > 1;
      this.classList.toggle("product-content--no-nav", !this.navigationEnabled);

      if (this.targets.length === 0) {
        const shopifySection = this.closest(".shopify-section");
        if (shopifySection) shopifySection.hidden = true;
        this.links = [];
        return;
      }

      if (!this.navigationEnabled) {
        if (anchorsNav) anchorsNav.hidden = true;
        this.links = [];
        return;
      }

      if (!navList) return;

      const fragment = document.createDocumentFragment();

      this.targets.forEach((section) => {
        const label =
          section.querySelector("h2")?.textContent.trim() || section.id;
        const listItem = document.createElement("li");
        listItem.className = "product-content__list-item";

        const link = document.createElement("a");
        link.href = `#${section.id}`;
        link.className = "product-content__link";
        link.textContent = label;

        listItem.appendChild(link);
        fragment.appendChild(listItem);
      });

      navList.appendChild(fragment);
      this.links = Array.from(this.querySelectorAll('a[href^="#"]'));
    }

    recomputeObserver = () => {
      this.observer?.disconnect();
      this.intersecting = new Set();

      const header = document.querySelector(".page-header");
      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      const anchorsNav = this.querySelector(".product-content__anchors");
      const navHeight = anchorsNav
        ? anchorsNav.getBoundingClientRect().height
        : 0;
      const activationOffset = Math.round(headerHeight + navHeight + 1);
      const viewport =
        window.innerHeight || document.documentElement.clientHeight;
      const bottomMargin = Math.max(0, viewport - activationOffset - 1);

      this.observer = new IntersectionObserver(this.handleObserve, {
        root: null,
        rootMargin: `-${activationOffset}px 0px -${bottomMargin}px 0px`,
        threshold: 0,
      });

      this.targets.forEach((target) => {
        this.observer.observe(target);
      });
    };

    handleObserve = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) this.intersecting.add(entry.target);
        else this.intersecting.delete(entry.target);
      });

      if (performance.now() < this.suppressObserverUntil) return;

      const active = this.targets.findLast((t) => this.intersecting.has(t));
      this.setActiveById(active ? active.getAttribute("id") : null);
    };

    setActiveById(id) {
      this.links.forEach((link) => {
        link.classList.toggle(
          "active",
          id !== null && link.getAttribute("href") === `#${id}`,
        );
      });
    }

    syncHeaderVar = () => {
      const header = document.querySelector(".page-header");
      if (header) {
        document.documentElement.style.setProperty(
          "--header-height",
          `${header.getBoundingClientRect().height}px`,
        );
      }
      this.recomputeObserver();
    };

    handleClick = (event) => {
      event.preventDefault();
      const targetElem = document.querySelector(
        event.currentTarget.getAttribute("href"),
      );
      if (!targetElem) return;

      const details = targetElem.querySelector("details");
      if (details) details.open = true;

      this.setActiveById(targetElem.getAttribute("id"));
      this.suppressObserverUntil = performance.now() + 800;

      targetElem.scrollIntoView({
        block: "start",
        inline: "nearest",
        behavior: "smooth",
      });
    };

    handleResize = () => {
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = requestAnimationFrame(() => {
        this.resizeRaf = 0;
        this.recomputeObserver();
      });
    };
  },
);
